class HoneycombHero extends HTMLElement {
  connectedCallback() {
    this.motionQuery ??= window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.removeEventListener?.('change', this.onMotionChange);
    this.motionQuery.addEventListener?.('change', this.onMotionChange);
    this.refresh();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.clearSectionSnap();
    this.railEvents?.abort();
    this.railResizeObserver?.disconnect();
    this.railVisibilityObserver?.disconnect();
    window.clearTimeout(this.railActivationTimer);
    window.clearTimeout(this.railLeaveTimer);
    this.railActivationRun = (this.railActivationRun || 0) + 1;
    this.removeRailRipples();
    this.railHydrationRun = (this.railHydrationRun || 0) + 1;
    this.motionQuery?.removeEventListener?.('change', this.onMotionChange);
  }

  refresh() {
    this.stopAutoplay();
    this.clearSectionSnap();
    this.railEvents?.abort();
    this.railResizeObserver?.disconnect();
    this.railVisibilityObserver?.disconnect();
    window.clearTimeout(this.railActivationTimer);
    this.railActivationRun = (this.railActivationRun || 0) + 1;
    this.removeRailRipples();
    this.splitHeading();
    this.slides = [...this.querySelectorAll('.cellaura-hero__slide')];
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.showSlide(this.currentIndex);
    this.setupRailGallery();
    this.applyMotionPreference();
    this.setupSectionSnap();
    this.startAutoplay();
  }

  setupSectionSnap() {
    this.clearSectionSnap();
    this.snapPanels = [...this.querySelectorAll(':scope > section')];
    this.snapWheelDelta = 0;
    this.snapLocked = false;

    if (
      this.snapPanels.length < 2 ||
      this.motionQuery.matches ||
      window.matchMedia('(max-width: 899px)').matches
    ) {
      return;
    }

    this.snapEvents = new AbortController();
    this.addEventListener('wheel', this.onSectionWheel, {
      passive: false,
      signal: this.snapEvents.signal,
    });
  }

  clearSectionSnap() {
    this.snapEvents?.abort();
    this.snapEvents = null;
    window.clearTimeout(this.snapLockTimer);
    window.clearTimeout(this.snapWheelResetTimer);
    this.snapLocked = false;
    this.snapWheelDelta = 0;
  }

  onSectionWheel = (event) => {
    if (
      this.snapLocked ||
      this.motionQuery.matches ||
      window.innerWidth <= 899 ||
      event.ctrlKey ||
      Math.abs(event.deltaX) > Math.abs(event.deltaY)
    ) {
      if (this.snapLocked) {
        event.preventDefault();
      }
      return;
    }

    const deltaMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
    const delta = event.deltaY * deltaMultiplier;
    if (!delta) return;

    const viewportCenter = window.innerHeight / 2;
    const currentIndex = this.snapPanels.reduce(
      (nearestIndex, panel, index) => {
        const rect = panel.getBoundingClientRect();
        const center = rect.top + Math.min(rect.height, window.innerHeight) / 2;
        const distance = Math.abs(center - viewportCenter);
        return distance < nearestIndex.distance ? { index, distance } : nearestIndex;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY },
    ).index;

    const direction = Math.sign(delta);
    const isLeavingSnapRange =
      (currentIndex === 0 && direction < 0) ||
      (currentIndex === this.snapPanels.length - 1 && direction > 0);

    if (isLeavingSnapRange) {
      this.snapWheelDelta = 0;
      return;
    }

    event.preventDefault();
    this.snapWheelDelta += delta;
    window.clearTimeout(this.snapWheelResetTimer);
    this.snapWheelResetTimer = window.setTimeout(() => {
      this.snapWheelDelta = 0;
    }, 140);

    if (Math.abs(this.snapWheelDelta) < 12) return;

    const targetIndex = Math.min(
      this.snapPanels.length - 1,
      Math.max(0, currentIndex + direction),
    );
    this.snapWheelDelta = 0;
    if (targetIndex === currentIndex) return;

    const target = this.snapPanels[targetIndex];
    this.snapLocked = true;
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    this.holdSectionSnapLock();
  };

  holdSectionSnapLock() {
    window.clearTimeout(this.snapLockTimer);
    this.snapLockTimer = window.setTimeout(() => {
      this.snapLocked = false;
    }, 680);
  }

  splitHeading() {
    const heading = this.querySelector('.metallic-text');
    if (!heading || heading.classList.contains('is-split')) return;

    const text = heading.textContent.trim();
    const fragment = document.createDocumentFragment();

    [...text].forEach((character, index) => {
      const outer = document.createElement('span');
      const inner = document.createElement('span');

      outer.className = 'char-outer';
      inner.className = 'char-inner';
      outer.style.setProperty('--char-index', index);
      inner.style.setProperty('--char-index', index);
      inner.textContent = character === ' ' ? '\u00a0' : character;
      outer.append(inner);
      fragment.append(outer);
    });

    heading.replaceChildren(fragment);
    heading.classList.add('is-split');
  }

  showSlide(index) {
    if (!this.slides?.length) return;

    this.currentIndex = index % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === this.currentIndex);
    });
  }

  startAutoplay() {
    if (
      this.dataset.motionEnabled === 'false' ||
      this.motionQuery.matches ||
      !this.slides ||
      this.slides.length <= 1
    ) {
      return;
    }

    const hero = this.querySelector('.cellaura-hero-custom');
    const speed = Number.parseInt(hero?.dataset.autoplaySpeed || '5000', 10);

    this.autoplayTimer = window.setInterval(() => {
      this.showSlide(this.currentIndex + 1);
    }, Number.isFinite(speed) ? speed : 5000);
  }

  stopAutoplay() {
    if (this.autoplayTimer == null) return;
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  setupRailGallery() {
    this.railGallery = this.querySelector('[data-hc-rail-gallery]');
    if (!this.railGallery) return;

    const track = this.railGallery.querySelector('[data-hc-rail-track]');
    const sequence = this.railGallery.querySelector('[data-hc-rail-sequence]');
    const viewport = this.railGallery.querySelector('.hc-rail-gallery__viewport');
    if (!track || !sequence || !viewport) return;

    const sourceSlides = [...sequence.querySelectorAll('[data-hc-rail-slide]')];
    sourceSlides.forEach((slide, index) => {
      slide.dataset.hcRailIndex = String(index);
      slide.tabIndex = 0;
      slide.removeAttribute('aria-hidden');
      slide.setAttribute('aria-label', slide.dataset.label || `Product finish ${index + 1}`);
      slide.setAttribute('aria-expanded', 'false');
    });

    this.rebuildRailLoopCopies(track, sequence, viewport);
    this.queueRailImageHydration(track);

    this.railResizeObserver = new ResizeObserver(() => {
      if (this.railGallery?.dataset.paused === 'true') return;
      this.rebuildRailLoopCopies(track, sequence, viewport);
      this.queueRailImageHydration(track);
    });
    this.railResizeObserver.observe(viewport);

    this.railVisibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.railGallery?.toggleAttribute('data-offscreen', !entry?.isIntersecting);
      },
      { rootMargin: '120px 0px' },
    );
    this.railVisibilityObserver.observe(this.railGallery);

    this.railEvents = new AbortController();
    const { signal } = this.railEvents;

    this.railGallery.querySelectorAll('[data-hc-rail-slide]').forEach((slide) => {
      slide.addEventListener(
        'pointerenter',
        () => this.warmRailSlide(slide),
        { signal },
      );
    });
    this.railGallery.addEventListener(
      'focusin',
      (event) => {
        const slide = event.target.closest?.('[data-hc-rail-slide]');
        if (slide) this.warmRailSlide(slide);
      },
      { signal },
    );
    this.railGallery.addEventListener(
      'pointerdown',
      (event) => {
        const slide = event.target.closest?.('[data-hc-rail-slide]');
        if (slide) this.railGallery.dataset.paused = 'true';
      },
      { signal },
    );
    this.railGallery.addEventListener(
      'click',
      (event) => {
        const slide = event.target.closest?.('[data-hc-rail-slide]');
        if (slide) this.toggleRailExpandedSlide(slide, event);
      },
      { signal },
    );
    this.railGallery.addEventListener(
      'keydown',
      (event) => {
        const slide = event.target.closest?.('[data-hc-rail-slide]');
        if (!slide) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleRailExpandedSlide(slide);
        } else if (event.key === 'Escape') {
          this.clearRailExpandedSlide();
        }
      },
      { signal },
    );
    this.railGallery.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch' || !this.railGallery.querySelector('.is-expanded')) return;

        window.clearTimeout(this.railLeaveTimer);
        const expandedFor = performance.now() - (this.railExpandedAt || 0);
        const delay = Math.max(0, 620 - expandedFor);
        const { clientX, clientY } = event;
        this.railLeaveTimer = window.setTimeout(() => {
          const elementUnderPointer = document.elementFromPoint(clientX, clientY);
          if (!elementUnderPointer || !this.railGallery.contains(elementUnderPointer)) {
            this.clearRailExpandedSlide();
          }
        }, delay);
      },
      { signal },
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (
          !this.railGallery.contains(event.target)
          && this.hasRailExpandedState()
        ) {
          this.clearRailExpandedSlide();
        }
      },
      { signal },
    );
    document.addEventListener(
      'visibilitychange',
      () => {
        this.railGallery.toggleAttribute('data-document-hidden', document.hidden);
      },
      { signal },
    );
  }

  getRailLoopProgress(track) {
    if (!track) return 0;
    const styles = getComputedStyle(track);
    const distance = Number.parseFloat(styles.getPropertyValue('--hc-rail-loop-distance')) || 0;
    if (!distance) return 0;

    let translateY = 0;
    try {
      translateY = new DOMMatrixReadOnly(styles.transform).m42;
    } catch {
      return 0;
    }

    return (((-translateY % distance) + distance) % distance) / distance;
  }

  setRailLoopTiming(track, distance, progress = 0) {
    if (!track || !distance) return;
    const pixelsPerSecond = 10;
    const duration = Math.max(36, distance / pixelsPerSecond);
    track.style.animation = 'none';
    track.style.transform = 'none';
    track.getBoundingClientRect();
    track.style.setProperty('--hc-rail-loop-distance', `${distance}px`);
    track.style.setProperty('--hc-rail-loop-duration', `${duration}s`);
    track.style.animationDelay = `${-progress * duration}s`;
    track.style.removeProperty('animation');
    track.style.removeProperty('transform');
  }

  rebuildRailLoopCopies(track, sequence, viewport) {
    const progress = this.getRailLoopProgress(track);
    track.querySelectorAll('[data-hc-rail-clone]').forEach((clone) => clone.remove());

    const sourceHeight = sequence.getBoundingClientRect().height;
    if (!sourceHeight || !viewport.clientHeight) return;

    const cloneCount = Math.ceil(viewport.clientHeight / sourceHeight) + 1;
    this.setRailLoopTiming(track, sourceHeight, progress);

    for (let copyIndex = 0; copyIndex < cloneCount; copyIndex += 1) {
      const clone = sequence.cloneNode(true);
      clone.removeAttribute('data-hc-rail-sequence');
      clone.setAttribute('data-hc-rail-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-hc-rail-slide]').forEach((slide, index) => {
        slide.dataset.hcRailIndex = String(index);
        slide.removeAttribute('tabindex');
        slide.removeAttribute('role');
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-expanded');
      });
      track.append(clone);
    }
  }

  queueRailImageHydration(container) {
    const groupedImages = new Map();
    [
      ...container.querySelectorAll(
        '[data-hc-rail-sequence] [data-hc-rail-foreground][data-src], [data-hc-rail-clone] [data-hc-rail-foreground][data-src]'
      ),
    ].forEach((image) => {
      const source = image.dataset.src;
      if (!source) return;
      if (!groupedImages.has(source)) groupedImages.set(source, []);
      groupedImages.get(source).push(image);
    });

    const groups = [...groupedImages.values()];
    groups.slice(0, 3).forEach((images) => {
      images.forEach((image) => this.hydrateRailImage(image));
    });

    const remaining = groups.slice(3);
    const hydrationRun = (this.railHydrationRun || 0) + 1;
    this.railHydrationRun = hydrationRun;
    const schedule = window.requestIdleCallback
      ? (callback) => window.requestIdleCallback(callback, { timeout: 1200 })
      : (callback) => window.setTimeout(callback, 48);

    const hydrateNext = () => {
      if (this.railHydrationRun !== hydrationRun) return;
      const images = remaining.shift();
      if (!images) return;
      images.forEach((image) => this.hydrateRailImage(image));
      schedule(hydrateNext);
    };

    schedule(hydrateNext);
  }

  hydrateRailImage(image) {
    const source = image?.dataset.src;
    if (!source) return;

    image.loading = 'lazy';
    image.decoding = 'async';
    image.addEventListener(
      'error',
      () => {
        const fallback = image.dataset.original;
        if (fallback && image.src !== fallback) image.src = fallback;
      },
      { once: true },
    );
    image.src = source;
    image.removeAttribute('data-src');
  }

  warmRailSlide(slide) {
    const image = slide?.querySelector('[data-hc-rail-foreground]');
    if (image?.dataset.src) this.hydrateRailImage(image);
    return image;
  }

  waitForRailSlideImage(slide) {
    const image = this.warmRailSlide(slide);
    if (!image || (image.complete && image.naturalWidth > 0)) return Promise.resolve();

    const imageReady =
      typeof image.decode === 'function'
        ? image.decode().catch(() => undefined)
        : new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });

    return Promise.race([
      imageReady,
      new Promise((resolve) => window.setTimeout(resolve, 520)),
    ]);
  }

  async toggleRailExpandedSlide(slide, event) {
    if (!slide || !this.railGallery?.contains(slide)) return;
    if (slide.classList.contains('is-expanded')) {
      this.clearRailExpandedSlide();
      return;
    }

    const activationRun = (this.railActivationRun || 0) + 1;
    this.railActivationRun = activationRun;
    this.railGallery.dataset.paused = 'true';
    this.railGallery.dataset.pending = 'true';
    this.freezeRailTrackPosition();
    this.playRailRipple(slide, event);
    await Promise.all([
      this.waitForRailSlideImage(slide),
      new Promise((resolve) =>
        window.setTimeout(resolve, this.motionQuery.matches ? 0 : 90),
      ),
    ]);
    if (this.railActivationRun !== activationRun || !this.railGallery?.contains(slide)) return;
    delete this.railGallery.dataset.pending;
    this.setRailExpandedSlide(slide);
  }

  freezeRailTrackPosition() {
    const viewport = this.railGallery?.querySelector('.hc-rail-gallery__viewport');
    const track = this.railGallery?.querySelector('[data-hc-rail-track]');
    if (!viewport || !track || track.style.animation === 'none') return;

    this.railFrozenProgress = this.getRailLoopProgress(track);
    let translateY = 0;
    try {
      translateY = new DOMMatrixReadOnly(getComputedStyle(track).transform).m42;
    } catch {
      translateY = 0;
    }
    track.style.animation = 'none';
    track.style.transform = 'none';
    viewport.scrollTop = Math.max(0, -translateY);
  }

  resumeRailTrackPosition(track) {
    const viewport = this.railGallery?.querySelector('.hc-rail-gallery__viewport');
    if (!track || !viewport) return;
    const styles = getComputedStyle(track);
    const distance = Number.parseFloat(styles.getPropertyValue('--hc-rail-loop-distance')) || 0;
    viewport.scrollTop = 0;
    this.setRailLoopTiming(track, distance, this.railFrozenProgress || 0);
    this.railFrozenProgress = 0;
  }

  playRailRipple(slide, event) {
    if (!slide || this.motionQuery.matches) return;
    const rect = slide.getBoundingClientRect();
    const hasPointerPosition = Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY);
    const rippleX = hasPointerPosition ? event.clientX - rect.left : rect.width / 2;
    const rippleY = hasPointerPosition ? event.clientY - rect.top : rect.height / 2;
    const ripple = document.createElement('span');
    ripple.className = 'hc-rail-gallery__ripple';
    ripple.setAttribute('aria-hidden', 'true');
    ripple.style.setProperty('--hc-ripple-x', `${Math.max(0, Math.min(rect.width, rippleX))}px`);
    ripple.style.setProperty('--hc-ripple-y', `${Math.max(0, Math.min(rect.height, rippleY))}px`);
    slide.querySelectorAll('.hc-rail-gallery__ripple').forEach((existingRipple) => existingRipple.remove());
    slide.append(ripple);

    const removeRipple = () => ripple.remove();
    ripple.addEventListener('animationend', removeRipple, { once: true });
    window.setTimeout(removeRipple, 760);
  }

  removeRailRipples() {
    this.querySelectorAll('.hc-rail-gallery__ripple').forEach((ripple) => ripple.remove());
  }

  setRailExpandedSlide(slide) {
    if (!slide || !this.railGallery?.contains(slide)) return;
    const index = slide.dataset.hcRailIndex;
    this.railExpandedAt = performance.now();
    window.clearTimeout(this.railLeaveTimer);
    this.railGallery.dataset.paused = 'true';
    this.railGallery.querySelectorAll('[data-hc-rail-slide]').forEach((candidate) => {
      const isExpanded = candidate.dataset.hcRailIndex === index;
      candidate.classList.toggle('is-expanded', isExpanded);
      if (!candidate.closest('[data-hc-rail-clone]')) {
        candidate.setAttribute('aria-expanded', String(isExpanded));
      }
      if (isExpanded) this.warmRailSlide(candidate);
    });
    this.centerRailExpandedSlide(slide);
  }

  centerRailExpandedSlide(slide) {
    if (this.motionQuery.matches) {
      this.centerRailTrackOnSlide(slide, 'auto');
      return;
    }
    slide.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'flex-basis' && slide.classList.contains('is-expanded')) {
          this.centerRailTrackOnSlide(slide, 'smooth');
        }
      },
      { once: true, signal: this.railEvents.signal },
    );
  }

  centerRailTrackOnSlide(slide, behavior) {
    const viewport = this.railGallery?.querySelector('.hc-rail-gallery__viewport');
    if (!viewport || !slide?.classList.contains('is-expanded')) return;

    const slideRect = slide.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    const offset = slideRect.top + slideRect.height / 2 - (viewportRect.top + viewportRect.height / 2);
    if (Math.abs(offset) < 8) return;
    viewport.scrollBy({ top: offset, behavior });
  }

  hasRailExpandedState() {
    if (!this.railGallery) return false;
    return Boolean(
      this.railGallery.querySelector('.is-expanded')
        || this.railGallery.dataset.pending === 'true',
    );
  }

  clearRailExpandedSlide() {
    if (!this.railGallery) return;
    if (!this.hasRailExpandedState()) return;
    this.railActivationRun = (this.railActivationRun || 0) + 1;
    window.clearTimeout(this.railActivationTimer);
    window.clearTimeout(this.railLeaveTimer);
    this.railLeaveTimer = null;
    delete this.railGallery.dataset.pending;
    this.railGallery.dataset.paused = 'false';
    this.railGallery.querySelectorAll('.is-expanded').forEach((slide) => {
      slide.classList.remove('is-expanded');
    });
    this.railGallery.querySelectorAll('[aria-expanded="true"]').forEach((slide) => {
      slide.setAttribute('aria-expanded', 'false');
    });
    const track = this.railGallery.querySelector('[data-hc-rail-track]');
    this.resumeRailTrackPosition(track);
    this.removeRailRipples();
  }

  applyMotionPreference() {
    const shouldPause = this.motionQuery.matches || this.dataset.motionEnabled === 'false';
    if (this.railGallery) {
      this.railGallery.toggleAttribute('data-reduced-motion', shouldPause);
    }

    this.querySelectorAll('.sleep-motion-icon svg').forEach((svg) => {
      if (shouldPause) svg.pauseAnimations?.();
      else svg.unpauseAnimations?.();
    });
  }

  onMotionChange = () => {
    this.stopAutoplay();
    this.applyMotionPreference();
    this.setupSectionSnap();
    this.startAutoplay();
  };
}

if (!customElements.get('honeycomb-hero')) {
  customElements.define('honeycomb-hero', HoneycombHero);
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector?.('honeycomb-hero');
  section?.refresh?.();
});
